import { DeleteIcon, EditIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { Box, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { FC, useContext, useState } from "react";

import { MenuAction, RanaTable, TablePagination } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";
import { Template, ThreediModel } from "@nens/threedi/openapi-client";

import { LIVE_SITE } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import { GridCard } from "../../components/GridCard";
import { Toast } from "../../components/Toast";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useOrganisationContract } from "../../queries/general";
import {
  useDeleteSimulationTemplate,
  useSimulationTemplates,
} from "../../queries/simulationTemplates";

import EditTemplateNameModal from "./EditTemplateNameModal";

interface SimulationTemplatesTableProps {
  templates: (Template & { uuid: string })[];
  deleteSimulationTemplate: (template: Template) => void;
}

const SimulationTemplatesTable: FC<SimulationTemplatesTableProps> = ({
  templates,
  deleteSimulationTemplate,
}) => {
  const { organisation } = useContext(OrganisationContext);
  const contract = useOrganisationContract(organisation.uniqueId);
  const includes3DiLive = contract?.scope?.includes("livesite");

  const toast = useToast();

  const showToast = (title: string, status: "success" | "error" | "info") => {
    toast({
      render: ({ onClose }) => <Toast title={title} status={status} onClose={onClose} />,
      duration: 9000,
      isClosable: true,
    });
  };

  const openIn3DiLive = async (row: Template) => {
    if (includes3DiLive && row.simulation?.threedimodelId && row.id) {
      window.open(
        `https://${LIVE_SITE}/quickstart/simulation-template/${row.simulation.threedimodelId}/${row.id}/${organisation.uniqueId}/`
      );
    } else if (!includes3DiLive) {
      showToast(
        "Option not included in contract. Please contact the service desk if you want to add Rana live to your contract.",
        "info"
      );
    } else {
      showToast("Cannot open template. Template is missing required information.", "error");
    }
  };

  const [templateName, setTemplateName] = useState("");
  const [templateId, setTemplateId] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const openRenameTemplateModal = (row: Template) => {
    setTemplateId(row.id ?? 0);
    setTemplateName(row.name);
    onOpen();
  };

  const getActions = (template: Template): MenuAction[] => [
    {
      label: "Run on Rana Live",
      icon: <ExternalLinkIcon />,
      action: () => openIn3DiLive(template),
    },
    {
      label: "Delete template",
      icon: <DeleteIcon />,
      action: () => deleteSimulationTemplate(template),
    },
    {
      label: "Rename Template",
      icon: <EditIcon />,
      action: () => openRenameTemplateModal(template),
    },
  ];

  return (
    <>
      <RanaTable
        columnDefinitions={[
          { width: "table_cell_xxs", title: "ID" },
          { width: "table_cell_xs", title: "Name" },
          { width: "table_cell_xs", title: "From simulation" },
        ]}
        tableDataIds={templates.map((template) => template.uuid)}
        hasRowActions
        emptyElement={<Text>There is no data to be shown with the current filters.</Text>}
        sx={{
          // vertical alignment for the icons and checkboxes
          "& p": {
            marginBottom: 0,
          },
          "& label": {
            marginBottom: 0,
          },
        }}
      >
        <RanaTable.Container minHeight="auto" height="auto">
          <RanaTable.Header />
          <RanaTable.Body>
            {templates.map((template) => (
              <RanaTable.Row
                rowActions={getActions(template)}
                key={template.uuid}
                rowId={template.uuid}
                to={`${APP_BASE_HREF}/simulations/${template.simulation?.id ?? ""}`}
              >
                <RanaTable.Cell>{template.id}</RanaTable.Cell>
                <RanaTable.Cell>{template.name}</RanaTable.Cell>
                <RanaTable.Cell>{template.simulation?.name}</RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
      {isOpen ? (
        <EditTemplateNameModal
          isOpen={isOpen}
          onClose={onClose}
          templateName={templateName}
          templateId={templateId}
        />
      ) : null}
    </>
  );
};

interface ModelSimulationTemplatesCardContentProps {
  threediModel: ThreediModel & { id: number };
}

const ModelSimulationTemplatesCardContent: FC<ModelSimulationTemplatesCardContentProps> = ({
  threediModel,
}) => {
  const { limit, offset, setPage, page, setPageSize } = useTableData({
    initialPageSize: 4,
  });

  const simulationTemplatesResponse = useSimulationTemplates(threediModel.id, {
    offset,
    limit,
  });
  const { deleteSimulationTemplate } = useDeleteSimulationTemplate(threediModel.id);

  if (!simulationTemplatesResponse.isSuccess || simulationTemplatesResponse.data.count === 0) {
    return null;
  }

  const templates = simulationTemplatesResponse.data.results.map((simulationTemplate) => {
    return { ...simulationTemplate, uuid: "" + simulationTemplate.id };
  });

  const totalCount = simulationTemplatesResponse.data.count;
  const numPages = Math.ceil(totalCount / limit);

  return (
    <GridCard colSpan={2} title="Simulation templates">
      <Box>
        <SimulationTemplatesTable
          templates={templates}
          deleteSimulationTemplate={deleteSimulationTemplate}
        />
        <Box mt={4}>
          <TablePagination
            limit={limit}
            numPages={numPages}
            page={page}
            setPageSize={setPageSize}
            setPage={setPage}
          />
        </Box>
      </Box>
    </GridCard>
  );
};

interface ModelSimulationTemplatesCardProps {
  threediModel: ThreediModel;
}

function hasDefinedId(model: ThreediModel): model is ThreediModel & { id: number } {
  return model.id !== undefined;
}

const ModelSimulationTemplatesCard: FC<ModelSimulationTemplatesCardProps> = ({ threediModel }) => {
  if (!hasDefinedId(threediModel)) {
    return null;
  }

  return <ModelSimulationTemplatesCardContent threediModel={threediModel} />;
};

export default ModelSimulationTemplatesCard;
